import { chunk } from 'lodash-es';
import { BreadthFirstTreeTraverser } from './BreadthFirstTreeTraverser';
import { ParallelBatchProcessor } from './ParallelBatchProcessor';

/**
 * Orchestrates breadth-first, batched, parallel processing of nodes in a tree.
 */
export class BreadthFirstTreeProcessor<T> {
  traverser: BreadthFirstTreeTraverser<T>;

  parallelBatcher: ParallelBatchProcessor<T>;

  batchSize: number;

  constructor(options: {
    batchSize: number;
    maxParallelBatches: number;
    getChildren: (node: T) => Promise<T[]>;
  }) {
    this.batchSize = options.batchSize;
    this.traverser = new BreadthFirstTreeTraverser<T>(options.getChildren);
    this.parallelBatcher = new ParallelBatchProcessor<T>(options.maxParallelBatches);
  }

  /**
   * Processes batches of nodes at each level (regardless of parent).
   * The callback receives the batch and the level index.
   */
  async processBatchInLevel(
    topLevelNodes: T[],
    processBatchForLevel: (batch: T[], levelIndex: number) => Promise<void>,
  ): Promise<void> {
    let levelIndex = 0;
    for await (const level of this.traverser.traverseLevel(topLevelNodes)) {
      const batches = chunk(level, this.batchSize);

      // Use ParallelBatchProcessor to respect maxParallelBatches
      await this.parallelBatcher.processBatches(batches, async batch => {
        await processBatchForLevel(batch, levelIndex);
      });
      levelIndex += 1;
    }
  }

  /**
   * Processes batches of sibling nodes (nodes with the same parent) at each level.
   * For each sibling group, batches are created and processed in parallel.
   * The callback receives the batch, the parent node (null for top-level nodes), and the level index.
   */
  async processSiblingBatchInLevel(
    topLevelNodes: T[],
    processBatchForSiblingGroup: (
      batch: T[],
      parent: T | null,
      levelIndex: number,
    ) => Promise<void>,
  ): Promise<void> {
    let levelIndex = 0;
    for await (const level of this.traverser.traverseLevel(topLevelNodes)) {
      // Build parent->children map for this level
      const parentMap = new Map<T | null, T[]>();
      const childToParent = this.traverser.getChildToParentMap();

      for (const node of level) {
        const parent = childToParent.get(node) ?? null;
        if (!parentMap.has(parent)) parentMap.set(parent, []);

        const arr = parentMap.get(parent);
        if (arr) arr.push(node);
      }

      // For each sibling group (same parent), batch and process using ParallelBatchProcessor
      const siblingGroups = Array.from(parentMap.entries());
      for (const [parent, siblings] of siblingGroups) {
        const batches = chunk(siblings, this.batchSize);

        await this.parallelBatcher.processBatches(batches, async batch => {
          await processBatchForSiblingGroup(batch, parent, levelIndex);
        });
      }
      levelIndex += 1;
    }
  }
}
